import {SiteType} from '@packages/systems/core/records/Site';
import {ExpansionProps} from '@packages/systems/render';
import {NormalizedAtomConfig} from '@packages/systems/wfdl';
import {RenderTrackingContextItem} from '@packages/systems/element-tracking-types';

/**
 * Strips the `type` field from the context items and serializes them.
 */
function serializeContextItems(items: RenderTrackingContextItem[]): string {
  return encodeURIComponent(
    JSON.stringify(
      items.map((item) => ({
        ...item,
        type: undefined,
      }))
    )
  );
}

/**
 * Core implementation of element tracking context transformation.
 * This is used by both SSR and Designer expansions.
 */
function transformWithElementTrackingContextCore<T extends ExpansionProps>(
  props: T,
  atom: NormalizedAtomConfig
): T {
  const {trackingContext, attributes, element} = props;

  if (!atom.autoEngagementTrackingAttribute) {
    return props;
  }

  let newAttributes = attributes
    .set('data-wf-ao-click-engagement-tracking', 'true')
    .set('data-wf-element-id', element.id);

  const componentContext = [];
  const cmsListContext = [];
  for (const trackingContextItem of trackingContext) {
    switch (trackingContextItem.type) {
      case 'cms-list':
        cmsListContext.push(trackingContextItem);
        break;
      case 'component':
        componentContext.push(trackingContextItem);
        break;
    }
  }

  if (cmsListContext.length) {
    newAttributes = newAttributes.set(
      'data-wf-cms-context',
      serializeContextItems(cmsListContext)
    );
  }
  if (componentContext.length) {
    newAttributes = newAttributes.set(
      'data-wf-component-context',
      serializeContextItems(componentContext)
    );
  }

  return {
    ...props,
    attributes: newAttributes,
  };
}

/**
 * SSR-specific transform that checks site settings before applying tracking.
 */
export const transformWithElementTrackingContext = <T extends ExpansionProps>(
  props: T,
  atom: NormalizedAtomConfig,
  site: SiteType
): T => {
  const collectingIntellimizeData = site.intellimizeDataCollectionEnabled;
  if (!collectingIntellimizeData) {
    return props;
  }
  return transformWithElementTrackingContextCore(props, atom);
};

/**
 * Designer-specific transform that applies tracking based on whether or not the user is in Analyze Mode.
 */
export const transformWithElementTrackingContextForDesigner = <
  T extends ExpansionProps,
>(
  props: T,
  atom: NormalizedAtomConfig,
  analyzeMode: boolean
): T => {
  if (!analyzeMode) {
    return props;
  }
  return transformWithElementTrackingContextCore(props, atom);
};
